/*
 * Sample prospects (P3). Each one carries its stage history, so the screens
 * can show how far it got, how long each step took and — when it failed —
 * the stage it dropped out at and why.
 */
import { daysFromNow } from "@/helpers/crm";
import { prospectQualification } from "@/mock/masters";

const ORGS = [
  ["Kilimo Fresh Exporters", "Agriculture", "MID", 1, "Website", [1], "Mary Atieno", "Finance Manager"],
  ["Upper Hill Medical Centre", "Healthcare", "MID", 2, "Referral", [1], "Dr. Paul Kamau", "Administrator"],
  ["Pwani Logistics", "Logistics", "ENT", 3, "LinkedIn", [1], "Hassan Ali", "HR Director"],
  ["Lakeview SACCO", "SACCO", "MID", 4, "Event", [2], "Rose Akinyi", "CEO"],
  ["Mwangaza Academy", "Education", "SME", 6, "Google Ads", [1], "James Njoroge", "Principal"],
  ["Savannah Hotels Group", "Hospitality", "ENT", 1, "Outbound call", [1], "Linda Mutiso", "Group HR"],
  ["Tumaini NGO", "NGO", "SME", 5, "Referral", [1], "Esther Wairimu", "Programs Lead"],
  ["Rift Valley Millers", "Manufacturing", "ENT", 5, "Agent / Partner", [1], "Daniel Rotich", "Finance Director"],
  ["Jamii Microfinance", "Banking & Finance", "MID", 2, "Website", [2], "Stella Oduya", "COO"],
  ["Kampala Retail Hub", "Retail", "MID", 7, "LinkedIn", [1], "Moses Ssali", "Operations"],
  ["Nyali Beach Resort", "Hospitality", "MID", 3, "Event", [1], "Fatma Said", "GM"],
  ["Thika Steel Works", "Manufacturing", "ENT", 6, "Outbound call", [1], "George Kimani", "HR Manager"],
  ["Imara Insurance Brokers", "Insurance", "SME", 2, "Google Ads", [1], "Caroline Were", "Partner"],
  ["County Health Services", "Government", "ENT", 4, "Event", [1], "Dr. Otieno Oloo", "Director"],
  ["Smart Duka Chain", "Retail", "MID", 1, "Website", [1], "Kevin Langat", "Owner"],
  ["Elimu Bora Schools", "Education", "MID", 5, "Referral", [1], "Purity Chege", "Bursar"],
  ["Mvuli Agro Inputs", "Agriculture", "SME", 4, "Walk-in", [2], "Joseph Odhiambo", "Director"],
  ["Coastline Freight", "Logistics", "MID", 3, "LinkedIn", [1], "Amina Juma", "HR Officer"],
  ["Westlands Dental", "Healthcare", "SME", 1, "Google Ads", [1], "Dr. Nancy Mbugua", "Owner"],
  ["Harambee Sacco", "SACCO", "ENT", 2, "Agent / Partner", [2], "Philip Kiptoo", "CEO"],
  ["Bidii Manufacturers", "Manufacturing", "MID", 6, "Website", [1], "Irene Nduta", "HR"],
  ["Safari Tours Ltd", "Hospitality", "SME", 1, "Referral", [1], "Ali Mohammed", "MD"],
  ["Equator Pharmacies", "Healthcare", "MID", 4, "Outbound call", [1], "Beatrice Auma", "Operations"],
  ["Kijani Solar", "Manufacturing", "SME", 5, "LinkedIn", [1], "Victor Mutua", "CFO"],
  ["Nairobi Bible College", "Education", "SME", 2, "Event", [1], "Rev. John Mbatia", "Registrar"],
  ["Sokoni Wholesalers", "Retail", "MID", 3, "Website", [1], "Halima Abdi", "Owner"],
  ["Gatsby Insurance", "Insurance", "MID", 1, "Google Ads", [1], "Martin Gitau", "HR"],
  ["Ruiru Flower Farm", "Agriculture", "ENT", 6, "Agent / Partner", [1], "Lilian Wangari", "HR Manager"],
];

// [stage, owner, failure, days old, last contact days ago, next action days from now]
const STATES = [
  ["new", 3, null, 2, null, 1],
  ["contacted", 1, null, 12, 3, 2],
  ["interested", 4, null, 20, 1, 4],
  ["qualified", 1, null, 31, 2, 1],
  ["converted", 5, null, 45, 9, null],
  ["disqualified", 3, { at: "contacted", code: "CON-01", note: "Called 5 times over 3 weeks, emails bounced." }, 40, 18, null],
  ["contacted", null, null, 6, 6, null],
  ["qualified", 2, null, 25, 1, 0],
  ["disqualified", 4, { at: "interested", code: "PRC-01", note: "Budget frozen until next financial year.", revisit_on: 60 }, 55, 20, null],
  ["interested", 5, null, 14, 5, -2],
  ["nurture", 4, null, 70, 30, 25],
  ["new", null, null, 1, null, null],
  ["disqualified", 3, { at: "new", code: "DAT-01", note: "Same company as Imara Brokers Ltd (PRS-0098)." }, 9, null, null],
  ["contacted", 1, null, 18, 9, -5],
  ["converted", 1, null, 60, 15, null],
  ["interested", 5, null, 22, 4, 3],
  ["disqualified", 4, { at: "interested", code: "FIT-01", note: "Only 6 staff; product is overkill.", revisit_on: 180 }, 35, 12, null],
  ["new", 4, null, 3, null, 2],
  ["disqualified", 5, { at: "qualified", code: "SOL-01", note: "Renewed with current vendor for 2 years.", revisit_on: 540 }, 50, 7, null],
  ["qualified", 2, null, 28, 2, 1],
  ["contacted", null, null, 11, 11, null],
  ["disqualified", 1, { at: "contacted", code: "TIM-01", note: "Asked to call back after their AGM.", revisit_on: -2 }, 65, 33, null],
  ["interested", 4, null, 16, 2, 6],
  ["new", 5, null, 4, null, 0],
  ["disqualified", 3, { at: "interested", code: "CON-02", note: "Board decides; no access to the board chair." }, 42, 15, null],
  ["contacted", 1, null, 8, 8, -1],
  ["converted", 2, null, 38, 6, null],
  ["nurture", 3, null, 90, 45, 10],
];

const PATH = ["new", "contacted", "interested", "qualified", "converted"];

const historyFor = (stage, failed, age) => {
  const reached = failed ? PATH.indexOf(failed.at) : PATH.indexOf(stage === "nurture" ? "interested" : stage);
  const steps = [];
  const span = Math.max(1, Math.floor(age / (reached + 2)));
  for (let i = 1; i <= reached; i++) {
    steps.push({ from: PATH[i - 1], to: PATH[i], at: daysFromNow(-age + span * i), days_in_previous: span });
  }
  if (failed) steps.push({ from: failed.at, to: "disqualified", at: daysFromNow(-Math.max(1, Math.floor(span / 2))), days_in_previous: span, reason: failed.code });
  if (stage === "nurture") steps.push({ from: "interested", to: "nurture", at: daysFromNow(-span), days_in_previous: span });
  return steps.reverse();
};

const qualificationFor = (stage) => {
  const level = { new: 1, contacted: 2, interested: 4, nurture: 4, qualified: 7, converted: 7, disqualified: 2 }[stage] ?? 0;
  return Object.fromEntries(prospectQualification.map((q, i) => [q.key, i < level]));
};

export const prospects = ORGS.map(([organisation, industry, segment, location_id, source, products, contact, title], i) => {
  const [stage, owner_id, failure, age, lastContact, next] = STATES[i];
  const n = String(i + 101).padStart(4, "0");
  const failed = failure
    ? { ...failure, revisit_on: failure.revisit_on !== undefined ? daysFromNow(failure.revisit_on) : null, on: daysFromNow(-Math.floor(lastContact / 2 || 1)), by: "Jane Wanjiku" }
    : null;
  return {
    id: i + 1,
    code: `PRS-${n}`,
    organisation,
    industry,
    segment,
    location_id,
    source,
    campaign: source === "Event" ? "HR Summit 2026" : source === "Google Ads" ? "Payroll Q3 Search" : null,
    product_ids: products,
    contact_name: contact,
    contact_title: title,
    phone: `+254 7${String(10 + i).padStart(2, "0")} ${String(300 + i * 7).padStart(3, "0")} ${String(100 + i * 13).slice(-3)}`,
    email: `${contact.split(" ").slice(-1)[0].toLowerCase()}@${organisation.split(" ")[0].toLowerCase()}.co.ke`,
    staff_count: { SME: 30, MID: 140, ENT: 650 }[segment] + i * 3,
    estimated_value: { SME: 300000, MID: 900000, ENT: 2400000 }[segment] + i * 15000,
    owner_id,
    stage,
    created_at: daysFromNow(-age),
    last_contact_at: lastContact === null ? null : daysFromNow(-lastContact),
    next_action: next === null ? null : { type: ["Call", "Email", "Meeting", "Visit"][i % 4], date: daysFromNow(next), note: "Follow up on interest" },
    qualification: qualificationFor(stage),
    score: Math.max(12, 88 - i * 3),
    failed,
    converted_lead: stage === "converted" ? `LED-${String(40 + i).padStart(4, "0")}` : null,
    history: historyFor(stage, failure, age),
  };
});

export const prospectById = (id) => prospects.find((p) => p.id === Number(id));

export const interactionsFor = (p) => {
  if (!p.last_contact_at) return [];
  const base = [
    { at: `${p.last_contact_at}T10:30:00`, type: "Call", title: `Call with ${p.contact_name}`, body: "Discussed current payroll process and pain points around statutory returns.", by: "Jane Wanjiku" },
    { at: `${daysFromNow(-8)}T14:00:00`, type: "Email", title: "Sent product brochure", body: "Shared the HR & Payroll brochure and pricing guide.", by: "Jane Wanjiku" },
    { at: `${daysFromNow(-12)}T09:15:00`, type: "Meeting", title: "Intro meeting at their office", body: "Met the finance manager and HR officer; interest in payroll and leave.", by: "Brian Otieno" },
  ];
  return base;
};

// Possible duplicates, as the check on the create screen would find them.
export const findDuplicates = ({ organisation = "", email = "", phone = "" }) => {
  const org = organisation.trim().toLowerCase();
  const mail = email.trim().toLowerCase();
  const tel = phone.replace(/\s/g, "");
  if (!org && !mail && !tel) return [];
  return prospects.filter(
    (p) =>
      (org.length > 3 && p.organisation.toLowerCase().includes(org)) ||
      (mail && p.email.toLowerCase() === mail) ||
      (tel.length > 6 && p.phone.replace(/\s/g, "") === tel),
  );
};
